import { debounceTime } from "rxjs/operators";
import {
  Component,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  inject,
  input,
  ChangeDetectionStrategy,
} from "@angular/core";

import { QuoteRepository } from "../quote.repository";
import { Quote } from "../quote.type";
import { quotePreviewType } from "./quote-preview-type.const";
import { Subscription } from "rxjs";
import { ManualQuote } from "../models/quote-manual.model";
import { NotifyService } from "../../notify/notify.service";
import { FclTariff } from "../models/quote-fcl-tariff.model";
import { eventType } from "../../notify/event.const";
import { EventService } from "../../shared/event/event.service";
import { AuthHttpConfig } from "../../security/authorization/auth-http-config";
import { TOAST } from "../../notify/toast/toast-type.enum";
import { BackendErrorPipe } from "../../shared/backend/backend-error.pipe";
import { environment } from "environments/environment";
import { MultiQuote } from "../models/quote-multi.model";
import { SpinnerComponent } from "../../ui/spinner/spinner.component";
import { PdfViewerModule } from "ng2-pdf-viewer";
import { version as pdfJsVersion } from "pdfjs-dist";
import { ButtonDirective } from "primeng/button";
import { UserService } from "../../user/user.service";
import { role } from "../../user/role.const";
import { ActivatedRoute } from "@angular/router";
import { DebugPanelService } from "../../shared/debug-panel/debug-panel.service";

// ng2-pdf-viewer falls back to the jsdelivr CDN unless this is set before the
// first <pdf-viewer> is constructed. Assets are not output-hashed, so the
// version query keeps a cached worker from mismatching an upgraded pdf.js.
(window as Window & { pdfWorkerSrc?: string }).pdfWorkerSrc =
  `/assets/pdfjs/pdf.worker.min.mjs?v=${pdfJsVersion}`;

@Component({
  selector: "app-quote-preview",
  templateUrl: "./quote-preview.component.html",
  styleUrls: ["./quote-preview.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [ButtonDirective, SpinnerComponent, PdfViewerModule],
})
export class QuotePreviewComponent implements OnInit, OnDestroy, OnChanges {
  private quoteRepository = inject(QuoteRepository);
  private notifyService = inject(NotifyService);
  private eventService = inject(EventService);
  private httpConfig = inject(AuthHttpConfig);
  private userService = inject(UserService);
  private route = inject(ActivatedRoute);
  private debugPanelService = inject(DebugPanelService);

  public readonly quote = input<Quote>(undefined);
  public readonly quoteLoading = input<boolean>(false);
  public quotePreviewType = quotePreviewType;
  public activePreview = "";
  public source;
  public loading = false;
  public rendered = false;
  public isFullHeight = true;
  public subscription = new Subscription();

  private fetchPreviewOnQuoteLoadingComplete = false;

  ngOnInit() {
    this.init();
    this.listenToEvents();
    this.resolveDebugMode();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.debugPanelService.reset();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      this.fetchPreviewOnQuoteLoadingComplete &&
      changes.quoteLoading &&
      !changes.quoteLoading.currentValue
    ) {
      this.fetchPreviewOnQuoteLoadingComplete = false;
      this.fetchPreview();
    }
  }

  public fetchPreviewCheck(): void {
    if (!this.quoteLoading()) {
      this.fetchPreview();
    } else {
      this.fetchPreviewOnQuoteLoadingComplete = true;
    }
  }

  public fetchPreview() {
    this.loading = true;
    this.rendered = false;
    this.source = null;
    this.isFullHeight = !(this.quote() instanceof MultiQuote);

    const quote = this.quote();
    if (quote instanceof FclTariff || quote instanceof ManualQuote) {
      // For FclTariff it should be URL
      this.subscription.add(
        this.quoteRepository.getPreview(quote.getId()).subscribe({
          next: (response) => {
            this.source = response.url;
            this.loading = false;
          },
          error: (error) => {
            this.notifyService.toast(
              TOAST.ERROR,
              <string>new BackendErrorPipe().transform(error),
            );
          },
        }),
      );
    } else {
      // For all the rest it should be an object
      this.source = {
        url:
          environment.apiUrl +
          `/quote/${quote.getId()}/pdf?rand=${Math.random()}`,
        httpHeaders: {
          Authorization: this.httpConfig.getPrefixedToken(),
        },
        withCredentials: true,
        // Every request regenerates a byte-different PDF; mixing range chunks corrupts the xref table.
        disableRange: true,
      };

      if (this.httpConfig.getImpersonatedUsername()) {
        this.source.httpHeaders["X-Switch-User"] =
          this.httpConfig.getImpersonatedUsername();
      }
    }
  }

  public afterLoadComplete() {
    this.loading = false;
  }

  // A page left over from the previous document must not mark the viewer
  // rendered: the relative container it unlocks breaks pdf.js viewer setup.
  public onPageRendered() {
    if (!this.loading) {
      this.rendered = true;
    }
  }

  private init() {
    if (this.quote() instanceof FclTariff) {
      this.activePreview = quotePreviewType.xls;
    } else {
      this.activePreview = quotePreviewType.pdf;
      this.fetchPreview();
    }
  }

  private listenToEvents() {
    this.subscription.add(
      this.eventService.eventBus
        .pipe(debounceTime(1000))
        .subscribe((incoming) => {
          if (
            incoming.event === eventType.quoteUpdated &&
            this.activePreview === quotePreviewType.pdf
          ) {
            this.fetchPreview();
          }
        }),
    );
  }

  private resolveDebugMode(): void {
    const hasDebugParam = this.route.snapshot.queryParamMap.has("debug");
    if (!hasDebugParam) {
      return;
    }
    this.userService.getUser().then((user) => {
      if (user?.hasRole(role.superAdmin)) {
        this.fetchDebugData();
      }
    });
  }

  private fetchDebugData(): void {
    const quoteId = this.quote()?.getId();
    if (!quoteId) {
      return;
    }
    this.debugPanelService.activate();
    this.subscription.add(
      this.quoteRepository.getDebugData(quoteId).subscribe({
        next: (data) => {
          this.debugPanelService.setData(data);
        },
        error: () => {
          this.debugPanelService.setLoadingError();
        },
      }),
    );
  }
}
